import React, { useState, useEffect } from 'react';
import DragAndDropList from '../../components/DragAndDropList';
import ResearchLayout from '../../components/ResearchLayout';
import { questionService } from '../../services/questionService';
import { protocolService } from '../../services/protocolService';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const ProgramAdminLinkQuestions = () => {
  // Batch unlink state and handlers
  const [selectedLinkedQuestions, setSelectedLinkedQuestions] = useState([]);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const handleLinkedQuestionToggle = (questionId) => {
    setSelectedLinkedQuestions(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleBatchUnlink = async () => {
    if (selectedLinkedQuestions.length === 0) return;
    setIsUnlinking(true);
    setError('');
    try {
      const response = await questionService.bulkUnlinkQuestions(selectedLinkedQuestions, selectedProtocol);
      // Backend now returns explicit unlink info: unlinked_count, unlinked_qids, blocked_qids, already_unlinked_qids
      const unlinkedCount = response.unlinked_count ?? (response.unlinked_qids ? response.unlinked_qids.length : 0);
      const blocked = response.blocked_qids || response.blocked_assoc_ids || [];
      const already = response.already_unlinked_qids || [];

      if (unlinkedCount > 0) {
        setSelectedLinkedQuestions([]);
        await loadUserData();
        await loadProtocolQuestions(selectedProtocol);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2500);
      } else if (blocked.length > 0) {
        setError(`Cannot unlink ${blocked.length} answered question(s): ${blocked.join(', ')}`);
      } else if (already.length > 0) {
        setError(`${already.length} question(s) were already unlinked.`);
        // Refresh to reflect current state
        await loadUserData();
        await loadProtocolQuestions(selectedProtocol);
      } else if (!response.success) {
        throw new Error(response.message || response.error || 'Failed to unlink questions');
      } else {
        // No rows removed but success true — show informational message
        setError('No questions were unlinked.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.error ||
                          error.message ||
                          'Failed to unlink questions. Please try again.';
      setError(errorMessage);
    } finally {
      setIsUnlinking(false);
    }
  };
  const { user } = useAuth();
  const [selectedProtocol, setSelectedProtocol] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [userProtocols, setUserProtocols] = useState([]);
  const [userQuestions, setUserQuestions] = useState([]);
  const [unlinkedQuestions, setUnlinkedQuestions] = useState([]);
  const [protocolQuestions, setProtocolQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load user's protocols and questions on component mount
  useEffect(() => {
    loadUserData();
  }, []);

  // Load protocol questions when selected protocol changes
  useEffect(() => {
    if (selectedProtocol) {
      loadProtocolQuestions(selectedProtocol);
    } else {
      setProtocolQuestions([]);
    }
  }, [selectedProtocol]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const [protocolsResponse, questionsResponse] = await Promise.all([
        protocolService.getProtocolsForLinking({ per_page: 1000 }),
        questionService.getQuestions({ per_page: 1000 })
      ]);

      setUserProtocols(protocolsResponse.protocols || []);
      setUserQuestions(questionsResponse.questions || []);
      console.log(`Loaded ${protocolsResponse.protocols?.length || 0} linkable protocols`);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadProtocolQuestions = async (protocolId) => {
    try {
      const response = await questionService.getQuestions({ protocol_id: protocolId, per_page: 100 });
      setProtocolQuestions(response.questions || []);
    } catch (error) {
      console.error('Error loading protocol questions:', error);
    }
  };

  // Store selected questions as objects for drag-and-drop
  const [selectedQuestionObjs, setSelectedQuestionObjs] = useState([]);

  // Keep selectedQuestionObjs in sync with selectedQuestions and unlinkedQuestions
  useEffect(() => {
    setSelectedQuestionObjs(selectedQuestions.map(id => unlinkedQuestions.find(q => q.id === id)).filter(Boolean));
  }, [selectedQuestions, unlinkedQuestions]);

  const handleQuestionToggle = (questionId) => {
    setSelectedQuestions(prev => {
      if (prev.includes(questionId)) {
        return prev.filter(id => id !== questionId);
      } else {
        return [...prev, questionId];
      }
    });
  };

  const handleLinkQuestions = async () => {
    if (!selectedProtocol || selectedQuestionObjs.length === 0) {
      setError('Please select a protocol and at least one question.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Send the ordered question IDs to the backend using append-only endpoint
      const orderedQuestionIds = selectedQuestionObjs.map((q) => q.id);
      console.log('Linking questions with append-only ordering:', orderedQuestionIds);

      const response = await questionService.bulkLinkQuestionsAppendOnly(
        parseInt(selectedProtocol),
        orderedQuestionIds // Send the ordered question IDs
      );

      if (response.success) {
        setShowSuccess(true);
        setSelectedQuestions([]);
        setSelectedQuestionObjs([]);
        // Reload data to reflect changes
        await loadUserData();
        await loadProtocolQuestions(selectedProtocol);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        throw new Error(response.message || 'Failed to link questions');
      }
    } catch (error) {
      console.error('Error linking questions:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to link questions. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnlinkQuestion = async (questionId) => {
    if (!selectedProtocol) {
      setError('Select a protocol before unlinking a question.');
      return;
    }

    try {
      setIsUnlinking(true);
      setError('');
      console.log(`Starting unlink operation for question ${questionId} from protocol ${selectedProtocol}`);

      // Use the new dedicated single unlink endpoint
      const response = await questionService.unlinkSingleQuestionFromProtocol(
        questionId,
        Number(selectedProtocol)
      );

      console.log('Unlink response received:', response);

      if (response.success) {
        setShowSuccess(true);
        console.log('Unlink successful, reloading data...');
        // Reload questions to reflect changes
        await loadProtocolQuestions(selectedProtocol);
        // Also reload unlinked questions since this question is now available
        await loadUserData();
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        // Handle specific error cases from backend
        if (response.error && response.error.includes('answered')) {
          setError('Cannot unlink question - it has already been answered in this protocol');
        } else if (response.already_unlinked) {
          setError('Question was already unlinked from this protocol');
          // Still refresh data to ensure UI is consistent
          await loadProtocolQuestions(selectedProtocol);
          await loadUserData();
        } else {
          throw new Error(response.message || response.error || 'Failed to unlink question');
        }
      }
    } catch (error) {
      console.error('Error unlinking question:', error);
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.error ||
                          error.message ||
                          'Failed to unlink question. Please try again.';
      setError(errorMessage);
    } finally {
      setIsUnlinking(false);
    }
  };

  // Filter available questions in JS
  useEffect(() => {
    if (!selectedProtocol) {
      setUnlinkedQuestions([]);
      return;
    }
    // Only show questions NOT already linked to the selected protocol
    const available = userQuestions.filter(
      q => !q.protocols || !q.protocols.some(p => p.id === parseInt(selectedProtocol))
    );
    setUnlinkedQuestions(available);
  }, [userQuestions, selectedProtocol]);

  return (
    <ResearchLayout pageTitle="Link Questions to Protocols">
      <div className="container-fluid">
        {/* Success Alert */}
        {showSuccess && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            <strong>Success!</strong> Questions linked to protocol successfully.
            <button type="button" className="btn-close" onClick={() => setShowSuccess(false)}></button>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-circle me-2"></i>
            <strong>Error!</strong> {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        <div className="row">
          {/* Link Questions Section */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-link me-2"></i>
                  Link Questions to Protocol
                </h5>
              </div>
              <div className="card-body p-4">
                {/* Protocol Selection */}
                <div className="mb-4">
                  <label htmlFor="protocol" className="form-label fw-bold">
                    <i className="fas fa-clipboard-list me-2 text-primary"></i>
                    Select Protocol *
                  </label>
                  <select
                    className="form-select form-select-lg"
                    id="protocol"
                    value={selectedProtocol}
                    onChange={(e) => setSelectedProtocol(e.target.value)}
                  >
                    <option value="">Choose a protocol...</option>
                    {userProtocols.map(protocol => (
                      <option key={protocol.id} value={protocol.id}>
                        {protocol.name}
                      </option>
                    ))}
                  </select>
                  {selectedProtocol && (
                    <div className="mt-2 p-3 bg-light rounded">
                      <small className="text-muted">
                        <strong>Description:</strong> {userProtocols.find(p => p.id === parseInt(selectedProtocol))?.description}
                      </small>
                    </div>
                  )}
                </div>

                {/* Unlinked Questions */}
                <div className="mb-4">
                  <label className="form-label fw-bold">
                    <i className="fas fa-question-circle me-2 text-primary"></i>
                    Available Questions ({unlinkedQuestions.length})
                  </label>
                  
                  {loading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted mt-2">Loading questions...</p>
                    </div>
                  ) : unlinkedQuestions.length > 0 ? (
                    <>
                      {/* Checkbox list for selection */}
                      <div className="border rounded p-3 mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {unlinkedQuestions.map(question => (
                          <div key={question.id} className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`question-${question.id}`}
                              checked={selectedQuestions.includes(question.id)}
                              onChange={() => handleQuestionToggle(question.id)}
                            />
                            <label className="form-check-label ms-2" htmlFor={`question-${question.id}`}>{question.text}</label>
                          </div>
                        ))}
                      </div>
                      {/* Drag and drop for selected questions */}
                      <div className="border rounded p-3 bg-light">
                        <label className="form-label fw-bold mb-2">Reorder Selected Questions (Drag & Drop)</label>
                        {selectedQuestionObjs.length > 0 ? (
                          <DragAndDropList
                            key={selectedQuestionObjs.map(q => q.id).join('-')}
                            items={selectedQuestionObjs}
                            setItems={items => {
                              setSelectedQuestionObjs(items);
                              setSelectedQuestions(items.map(q => q.id));
                            }}
                            droppableId="selected-questions-dnd"
                            renderItem={(q, idx) => (
                              <div className="d-flex align-items-center">
                                <span className="me-2 text-muted">{idx + 1}.</span>
                                <span>{q.text}</span>
                              </div>
                            )}
                          />
                        ) : (
                          <div className="text-muted">No questions selected.</div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4 border rounded bg-light">
                      <i className="fas fa-question-circle fa-3x text-muted mb-3"></i>
                      <h6 className="text-muted">No Unlinked Questions Available</h6>
                      <p className="text-muted mb-0">All your questions are already linked to protocols, or create new questions first.</p>
                    </div>
                  )}
                </div>

                {/* Link Button */}
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    <i className="fas fa-info-circle me-2"></i>
                    {selectedQuestions.length} question(s) selected
                  </div>
                  
                  <button
                    type="button"
                    className="btn btn-primary btn-lg px-4"
                    onClick={handleLinkQuestions}
                    disabled={isSubmitting || !selectedProtocol || selectedQuestions.length === 0}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Linking...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-link me-2"></i>
                        Link Questions
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Protocol Summary */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <i className="fas fa-chart-bar me-2 text-primary"></i>
                  Linking Summary
                </h6>
              </div>
              <div className="card-body">
                <div className="text-center mb-3">
                  <div className="h4 text-primary">{userProtocols.length}</div>
                  <div className="text-muted">Total Protocols</div>
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Total Questions</span>
                    <span className="badge bg-info">{userQuestions.length}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Linked Questions</span>
                    <span className="badge bg-success">{userQuestions.filter(q => q.protocol_id).length}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Unlinked Questions</span>
                    <span className="badge bg-warning">{unlinkedQuestions.length}</span>
                  </div>
                </div>

                <div className="border-top pt-3">
                  <h6 className="text-muted mb-2">Protocol Status</h6>
                  {userProtocols.map(protocol => (
                    <div key={protocol.id} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-truncate" style={{ maxWidth: '150px' }}>
                          {protocol.name}
                        </small>
                        <span className={`badge ${protocol.question_count > 0 ? 'bg-success' : 'bg-secondary'}`}>
                          {protocol.question_count || 0} questions
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Current Protocol Questions */}

        {selectedProtocol && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    <i className="fas fa-list me-2 text-primary"></i>
                    Questions in "{userProtocols.find(p => p.id === parseInt(selectedProtocol))?.name}"
                    ({protocolQuestions.length})
                  </h6>
                  {/* Batch Unlink Button */}
                  {protocolQuestions.length > 0 && (
                    <button
                      className="btn btn-danger btn-sm"
                      disabled={selectedLinkedQuestions.length === 0 || isUnlinking}
                      onClick={handleBatchUnlink}
                    >
                      {isUnlinking ? (
                        <span className="spinner-border spinner-border-sm me-2"></span>
                      ) : (
                        <i className="fas fa-unlink me-2"></i>
                      )}
                      Unlink Selected
                    </button>
                  )}
                </div>
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-success" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted mt-2">Loading protocol questions...</p>
                    </div>
                  ) : protocolQuestions.length > 0 ? (
                    <div className="row">
                      {protocolQuestions.map((question, index) => (
                        <div key={question.id} className="col-lg-6 mb-3">
                          <div className="card border border-success">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <input
                                    type="checkbox"
                                    className="form-check-input me-2"
                                    checked={selectedLinkedQuestions.includes(question.id)}
                                    onChange={() => handleLinkedQuestionToggle(question.id)}
                                    id={`linked-question-checkbox-${question.id}`}
                                  />
                                  <span className="badge bg-primary me-2">Q{index + 1}</span>
                                  <span className={`badge ${
                                    question.type === 'mcq' ? 'bg-info' :
                                    question.type === 'text' ? 'bg-warning text-dark' :
                                    question.type === 'range' ? 'bg-warning' :
                                    'bg-secondary'
                                  }`}>
                                    {question.type === 'mcq' ? 'MCQ' :
                                     question.type === 'boolean' ? 'T/F' :
                                     question.type === 'range' ? 'Range' :
                                     'Text'}
                                  </span>
                                </div>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleUnlinkQuestion(question.id)}
                                  title="Unlink question"
                                >
                                  <i className="fas fa-unlink"></i>
                                </button>
                              </div>
                              <h6 className="text-dark">{question.text}</h6>
                              <div className="mb-2">
                                <small className="text-muted">
                                  <i className="fas fa-tag me-1"></i>
                                  {question.category}
                                </small>
                              </div>
                              {question.choices && question.choices.length > 0 && (
                                <div className="mt-2">
                                  <small className="text-muted d-block mb-1">Options:</small>
                                  <div className="small">
                                    {question.choices.map((choice, idx) => (
                                      <span key={idx} className="badge bg-light text-dark me-1 mb-1">
                                        {String.fromCharCode(65 + idx)}: {choice}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <i className="fas fa-link fa-3x text-muted mb-3"></i>
                      <h6 className="text-muted">No Questions Linked Yet</h6>
                      <p className="text-muted">Select questions above to link them to this protocol.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </ResearchLayout>
  );
};

export default ProgramAdminLinkQuestions;